// ** react imports
import React from 'react';

// ** enums imports
import { BADGE_THEMES, BADGE_SIZES } from '../../../enums/common';

// Format text like "in_progress" or "created" to "In Progress" / "Created"
const formatBadgeText = txt => {
  if (txt === undefined || txt === null) return '';
  const str = String(txt).replace(/[_-]+/g, ' ').trim();
  return str.replace(/\b\w/g, l => l.toUpperCase());
};

const Badge = ({
  theme = BADGE_THEMES.DEFAULT,
  size = BADGE_SIZES.SM,
  text,
  className = '',
  children,
  ...props
}) => {
  // Define size classes
  const sizeClasses = {
    [BADGE_SIZES.XS]: 'px-1.5 py-0.5 text-xs',
    [BADGE_SIZES.SM]: 'px-2 py-0.5 text-xs',
    [BADGE_SIZES.MD]: 'px-2.5 py-0.5 text-sm',
    [BADGE_SIZES.LG]: 'px-3 py-1 text-sm',
  };

  // Define theme classes
  const themeClasses = {
    [BADGE_THEMES.DEFAULT]:
      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    [BADGE_THEMES.PRIMARY]:
      'bg-violet-100 text-violet-800 dark:bg-violet-500/30 dark:text-violet-300',
    [BADGE_THEMES.SUCCESS]:
      'bg-green-100 text-green-800 dark:bg-green-500/30 dark:text-green-300',
    [BADGE_THEMES.WARNING]:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/30 dark:text-yellow-300',
    [BADGE_THEMES.DANGER]:
      'bg-red-100 text-red-800 dark:bg-red-500/30 dark:text-red-300',
    [BADGE_THEMES.INFO]:
      'bg-blue-100 text-blue-800 dark:bg-blue-500/30 dark:text-blue-300',
    [BADGE_THEMES.SECONDARY]:
      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    [BADGE_THEMES.LIGHT]:
      'bg-gray-50 text-gray-700 dark:bg-gray-600 dark:text-gray-200',
    [BADGE_THEMES.DARK]:
      'bg-gray-800 text-gray-100 dark:bg-gray-900 dark:text-gray-200',
  };

  // Status-specific themes (for file status, section status, etc.)
  const statusThemeClasses = {
    // File Status themes
    CREATED: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    IN_PROGRESS:
      'bg-blue-100 text-blue-800 dark:bg-blue-500/30 dark:text-blue-300',
    COMPLETED:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/30 dark:text-emerald-300',
    SUBMITTED:
      'bg-purple-100 text-purple-800 dark:bg-purple-500/30 dark:text-purple-300',
    PROCESSED:
      'bg-green-100 text-green-800 dark:bg-green-500/30 dark:text-green-300',
    ARCHIVED:
      'bg-amber-100 text-amber-800 dark:bg-amber-500/30 dark:text-amber-300',

    // Section Status themes
    completed:
      'bg-green-100 text-green-800 dark:bg-green-500/30 dark:text-green-300',
    in_progress:
      'bg-blue-100 text-blue-800 dark:bg-blue-500/30 dark:text-blue-300',
    not_started:
      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',

    // User Status themes
    ACTIVE:
      'bg-green-100 text-green-800 dark:bg-green-500/30 dark:text-green-300',
    INACTIVE: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    PENDING:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/30 dark:text-yellow-300',
    SUSPENDED: 'bg-red-100 text-red-800 dark:bg-red-500/30 dark:text-red-300',

    // Quiz Status themes
    DRAFT:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/30 dark:text-yellow-300',
    PUBLISHED:
      'bg-green-100 text-green-800 dark:bg-green-500/30 dark:text-green-300',
    DELETED: 'bg-red-100 text-red-800 dark:bg-red-500/30 dark:text-red-300',

    // Boolean status
    true: 'bg-green-100 text-green-800 dark:bg-green-500/30 dark:text-green-300',
    false: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };

  // Special case badges
  const specialBadges = {
    walk_in:
      'bg-orange-100 text-orange-600 dark:bg-orange-400/30 dark:text-orange-300',
    exclusive:
      'bg-purple-100 text-purple-600 dark:bg-purple-400/30 dark:text-purple-300',
    secure: 'bg-gray-100 text-gray-600 dark:bg-gray-400/30 dark:text-gray-300',
    conditional:
      'bg-blue-100 text-blue-600 dark:bg-blue-400/30 dark:text-blue-300',
    opted_out:
      'bg-yellow-100 text-yellow-600 dark:bg-yellow-400/30 dark:text-yellow-300',
    required: 'bg-red-100 text-red-600 dark:bg-red-400/30 dark:text-red-300',
    invalid:
      'bg-orange-100 text-orange-600 dark:bg-orange-400/30 dark:text-orange-300',
    admin_modified:
      'bg-purple-100 text-purple-600 dark:bg-purple-400/30 dark:text-purple-300',
    error: 'bg-red-100 text-red-600 dark:bg-red-400/30 dark:text-red-300',
  };

  // Determine which theme class to use
  let badgeThemeClass = '';

  // Check if it's a status theme
  if (statusThemeClasses[theme]) {
    badgeThemeClass = statusThemeClasses[theme];
  }
  // Check if it's a special badge
  else if (specialBadges[theme]) {
    badgeThemeClass = specialBadges[theme];
  }
  // Use regular theme
  else {
    badgeThemeClass = themeClasses[theme] || themeClasses[BADGE_THEMES.DEFAULT];
  }

  const sizeClass = sizeClasses[size] || sizeClasses[BADGE_SIZES.SM];

  const badgeClasses = `inline-flex items-center rounded-full font-medium ${sizeClass} ${badgeThemeClass} ${className}`;

  return (
    <span className={badgeClasses} {...props}>
      {text !== undefined && text !== null ? formatBadgeText(text) : children}
    </span>
  );
};

export default Badge;
